import { ObjectId, ObjectIdSchema } from '@racemap/sdk/schema/base';
import type { TrackerTag, TrackerTagPrototype } from '@racemap/sdk/schema/trackerTags';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { isObjectChanged } from '@racemap/utilities/functions/isObjectChanged';
import { generateHexColorCode } from '@racemap/utilities/functions/random';
import { castDraft, produce } from 'immer';
import { Tristate } from 'src/components/utils/tristateTask';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import { Filters } from '../../../components/TrackerManager/Filter/FilterTreeFunctions';
import type { DraftState, State } from '../../reducers';
import type { TrackersActions } from '../trackers_reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const createTagActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<
  TrackersActions,
  | 'addTrackerTag'
  | 'updateOrCreateTrackerTag'
  | 'removeTrackerTag'
  | 'updateTrackerTag'
  | 'addTrackersToTag'
  | 'removeTrackersFromTag'
> => ({
  addTrackerTag: async (trackerIds, tagLabel) => {
    const tempObjectId = new ObjectId();
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );
      const tag: TrackerTagPrototype = {
        trackerIds,
        label: tagLabel,
        color: generateHexColorCode(),
      };

      const creatorId = get().users.currentUserId;
      set(
        produce((s: DraftState) => {
          s.trackers.trackerTags.set(tempObjectId.toHexString(), {
            ...tag,
            id: tempObjectId,
            creatorId: creatorId != null ? ObjectIdSchema.parse(creatorId) : new ObjectId(),
            editorIds: [],
            createdAt: new Date(),
            updatedAt: new Date(),
          });
          s.trackers.filterCounts
            .get(Filters.Tags)
            ?.set(tempObjectId.toHexString(), trackerIds.length);
        }),
      );

      const createdTrackerTag = await apiClient.createTrackerTag(tag);

      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.NORMAL;
          s.trackers.trackerTags.delete(tempObjectId.toHexString());
          s.trackers.trackerTags.set(createdTrackerTag.id.toHexString(), createdTrackerTag);
          s.trackers.filterCounts.get(Filters.Tags)?.delete(tempObjectId.toHexString());
          s.trackers.filterCounts
            .get(Filters.Tags)
            ?.set(createdTrackerTag.id.toHexString(), trackerIds.length);
        }),
      );

      toast.success('Tracker tag added successfully.');
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
          s.trackers.trackerTags.delete(tempObjectId.toHexString());
          s.trackers.filterCounts.get(Filters.Tags)?.delete(tempObjectId.toHexString());
        }),
      );

      if (error instanceof HTTPFetchError) {
        console.log(`Failed to add tracker tag: ${error.serverError}`);
        toast.error(
          `Failed to add tracker tag: ${
            error.serverError?.startsWith('MongoError: E11000')
              ? `Tag label "${tagLabel}" can only be used once.`
              : error.serverError
          }`,
        );
      } else if (error instanceof Error) {
        console.log(`Failed to add tracker tag: ${error.message}`);
        toast.error('Failed to add tracker tag.');
      }
    }
  },
  updateOrCreateTrackerTag: async (selectedTag, trackers) => {
    if (!selectedTag || !trackers) return;

    const existingTag = get().trackers.trackerTags.get(selectedTag);
    const trackerIds = trackers.map((tracker) => tracker.id);

    if (!existingTag) {
      get().trackers.actions.addTrackerTag(trackerIds, selectedTag);
    } else {
      get().trackers.actions.addTrackersToTag(existingTag.id, trackerIds);
    }
  },
  removeTrackerTag: async (tagId) => {
    const tagToRemove = get().trackers.trackerTags.get(tagId.toHexString());
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
          s.trackers.trackerTags.delete(tagId.toHexString());
        }),
      );
      await apiClient.deleteTrackerTag(tagId);
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );

      toast.success('Removed tracker tag.');
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
          tagToRemove != null &&
            s.trackers.trackerTags.set(tagToRemove.id.toHexString(), castDraft(tagToRemove));
        }),
      );
      if (error instanceof Error) {
        console.log(`Failed to remove tracker tag: ${error.message}`);
        toast.error('Failed to remove tracker tag.');
      }
    }
  },

  updateTrackerTag: async (tagId, trackerTagUpdate) => {
    const oldTrackerTag = get().trackers.trackerTags.get(tagId.toHexString());
    if (oldTrackerTag == null) return;

    const newTrackerTag = {
      ...oldTrackerTag,
      ...trackerTagUpdate,
    };

    if (!isObjectChanged(oldTrackerTag, newTrackerTag)) {
      toast.info('Nothing to update.');
      return;
    }

    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
          s.trackers.trackerTags.set(tagId.toHexString(), castDraft(newTrackerTag));
          s.trackers.filterCounts
            .get(Filters.Tags)
            ?.set(tagId.toHexString(), newTrackerTag.trackerIds.length);
        }),
      );
      const updatedTrackerTag = await apiClient.updateTrackerTag(tagId, trackerTagUpdate);

      toast.success('Tracker tag updated successfully.');
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.NORMAL;
          s.trackers.trackerTags.set(tagId.toHexString(), updatedTrackerTag);
        }),
      );
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
          s.trackers.trackerTags.set(tagId.toHexString(), castDraft(oldTrackerTag));
          s.trackers.filterCounts
            .get(Filters.Tags)
            ?.set(tagId.toHexString(), oldTrackerTag.trackerIds.length);
        }),
      );
      if (error instanceof Error) {
        console.log(`Failed to update tracker tag: ${error.message}`);
        toast.error('Failed to update tracker tag.');
      }
    }
  },

  addTrackersToTag: (tagId, trackerIds) =>
    saveTagTrackers(set, tagId, () => apiClient.addTrackersToTag(tagId, trackerIds)),
  removeTrackersFromTag: (tagId, trackerIds) =>
    saveTagTrackers(set, tagId, () => apiClient.removeTrackersFromTag(tagId, trackerIds)),
});

// ponytail: one queue per tag so a late response cannot overwrite a newer one; entries are never pruned
const tagQueues = new Map<string, Promise<void>>();

const saveTagTrackers = (
  set: StoreApi<State>['setState'],
  tagId: ObjectId,
  request: () => Promise<TrackerTag>,
) => {
  const key = tagId.toHexString();
  const next = (tagQueues.get(key) ?? Promise.resolve()).then(() =>
    runTagTrackersRequest(set, tagId, request),
  );
  tagQueues.set(key, next);
  return next;
};

const runTagTrackersRequest = async (
  set: StoreApi<State>['setState'],
  tagId: ObjectId,
  request: () => Promise<TrackerTag>,
) => {
  try {
    set(
      produce((s: DraftState) => {
        s.trackers.savingState = Tristate.SPINNING;
      }),
    );
    const updatedTag = await request();

    toast.success('Tracker tag updated successfully.');
    set(
      produce((s: DraftState) => {
        s.trackers.savingState = Tristate.NORMAL;
        s.trackers.trackerTags.set(tagId.toHexString(), castDraft(updatedTag));
        s.trackers.filterCounts
          .get(Filters.Tags)
          ?.set(tagId.toHexString(), updatedTag.trackerIds.length);
      }),
    );
  } catch (error) {
    set(
      produce((s: DraftState) => {
        s.trackers.savingState = Tristate.FAILURE;
      }),
    );
    if (error instanceof Error) {
      console.log(`Failed to update tracker tag: ${error.message}`);
      toast.error('Failed to update tracker tag.');
    }
  }
};
